import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, type Location } from "react-router";
import { twJoin } from "tailwind-merge";

import { Button } from "@/components/Button";
import { FullScreenLayout } from "@/components/FullScreenLayout";
import { Water } from "@/components/Water";
import { useBreakpoint } from "@/hooks/useMediaQuery";

import { ForgotPasswordForm } from "./components/ForgotPasswordForm";
import { LoginForm } from "./components/LoginForm";
import { SignupForm } from "./components/SignupForm";

type SplashPageForm = "default" | "login" | "signup" | "forgot-password";
type LocationState = { showingForm?: SplashPageForm };

export function SplashPage() {
  const { t } = useTranslation();
  // oxlint-disable-next-line no-unsafe-assignment
  const location: Location<LocationState | null> = useLocation();
  const [showingForm, setShowingForm] = useState<SplashPageForm>(location.state?.showingForm ?? "default");
  const breakpointSm = useBreakpoint("sm");

  return (
    <FullScreenLayout>
      <div
        className={twJoin(
          "isolate grid min-h-screen w-screen justify-between",
          'sm:[grid-template:"title_empty"_auto_"title_form"_auto_"trailer_form"_auto_"scroll_scroll"_1fr_/_1fr_auto]',
          '[grid-template:"title"_auto_"form"_auto_"scroll"_1fr_/_1fr]',
        )}
      >
        <div className="col-span-full row-[1_/_3] h-screen w-screen sm:row-[1_/_4]" />
        <div className="col-span-full row-[2_/_4] hidden sm:block">
          <Water width={100} height={100} start={90} end={0} bends={0} shore="top" />
        </div>
        <div className="col-span-full row-[2_/_3] sm:hidden">
          <Water width={100} height={100} start={20} end={20} bends={1} shore="top" />
        </div>

        <div className="flex flex-col gap-4 p-4 [grid-area:title] md:gap-32 md:p-16">
          <header>
            <h1 className="m-0 font-supertitle">{t("title")}</h1>
            <p className="m-0 font-title">{t("subtitle")}</p>
          </header>
          <div className="flex max-w-[32ch] flex-col gap-4 font-title">
            <Trans i18nKey="splash-blurb" />
          </div>
        </div>

        <div className="relative mt-auto hidden p-16 [grid-area:trailer] sm:block">
          <p className="mt-auto font-title">{t("splash-trailer")}</p>
        </div>

        <div className="@container mt-auto flex w-full flex-row overflow-hidden [grid-area:form] sm:w-90 md:w-120">
          <div
            className={twJoin(
              "flex flex-row items-end transition duration-150",
              showingForm === "signup" && "-translate-x-1/4 sm:translate-x-0",
              showingForm === "login" && "-translate-x-1/2 sm:-translate-x-1/3",
              showingForm === "forgot-password" && "-translate-x-3/4 sm:-translate-x-2/3",
              showingForm === "default" && "translate-x-0",
            )}
          >
            <section
              className="flex w-[100cqw] flex-col gap-4 p-4 sm:hidden"
              aria-label={t("sign-in-menu")}
              aria-hidden={showingForm !== "default"}
              inert={showingForm !== "default"}
            >
              <p className="mt-auto text-center font-title">{t("splash-trailer")}</p>
              <Button type="button" onClick={() => setShowingForm("signup")}>
                {t("sign-up")}
              </Button>
              <Button type="button" onClick={() => setShowingForm("login")}>
                {t("sign-in")}
              </Button>
            </section>

            {/* oxlint-disable jsx-no-literals */}
            <section
              className="flex w-[100cqw] flex-col px-8 py-4 md:p-16"
              aria-label={t("sign-up")}
              aria-hidden={showingForm !== "signup" && !(breakpointSm && showingForm === "default")}
              inert={showingForm !== "signup" && !(breakpointSm && showingForm === "default")}
            >
              <button
                type="button"
                className="mr-auto cursor-pointer font-hint sm:hidden"
                onClick={() => setShowingForm("default")}
              >
                ← {t("back")}
              </button>
              <button
                type="button"
                className="ml-auto hidden cursor-pointer font-hint sm:block"
                onClick={() => setShowingForm("login")}
              >
                {t("to-login")} →
              </button>
              <SignupForm />
            </section>

            <section
              className="flex w-[100cqw] flex-col px-8 py-4 md:p-16"
              aria-label={t("sign-in")}
              aria-hidden={showingForm !== "login"}
              inert={showingForm !== "login"}
            >
              <button
                type="button"
                className="mr-auto cursor-pointer font-hint sm:hidden"
                onClick={() => setShowingForm("default")}
              >
                ← {t("back")}
              </button>
              <button
                type="button"
                className="mr-auto hidden cursor-pointer font-hint sm:block"
                onClick={() => setShowingForm("signup")}
              >
                ← {t("to-signup")}
              </button>
              <LoginForm onForgotPassword={() => setShowingForm("forgot-password")} />
            </section>

            <section
              className="flex w-[100cqw] flex-col px-8 py-4 md:p-16"
              aria-label={t("forgot-password")}
              aria-hidden={showingForm !== "forgot-password"}
              inert={showingForm !== "forgot-password"}
            >
              <button
                type="button"
                className="mr-auto cursor-pointer font-hint"
                onClick={() => setShowingForm("login")}
              >
                ← {t("back")}
              </button>
              <ForgotPasswordForm />
            </section>
            {/* oxlint-enable jsx-no-literals */}
          </div>
        </div>
      </div>
    </FullScreenLayout>
  );
}
